import { useEffect } from 'react';
import { Ico, autoImgs, burnerImgs } from '../shared';
import { PageHeader, SectionCTA } from '../Layout';

export default function IndustriesPage() {
  useEffect(() => {
    document.title = 'Industries | Ratnam Techo Inc. – Who We Serve';
  }, []);

  return (
    <>
      <PageHeader
        title="Industries"
        highlight="We Serve"
        sub="From glass artists to automotive manufacturers — we engineer precision solutions for every industry."
        crumb="Industries"
      />

      <GlassArtists />
      <Manufacturing />
      <ResearchDev />
      <CustomInnovators />
      <SectionCTA heading="Serving Your Industry?" sub="Reach out today and let us tailor a solution specifically for your field." />
    </>
  );
}

function IndustrySection({ icon, iconColor = 'var(--blue)', tag, title, highlight, color = 'blue', desc, bullets, imgs, reverse, bg }) {
  return (
    <section className="section" style={{ background: bg || 'var(--white)' }}>
      <div className="section-inner">
        <div className={`service-detail-grid${reverse ? ' service-detail-grid-rev' : ''}`}>
          {!reverse && (
            <div className="service-detail-imgs">
              <img src={imgs[0]} alt={title} className="sd-img-main" />
              <div className="sd-img-row">
                <img src={imgs[1]} alt={`${title} 2`} className="sd-img-thumb" />
                <img src={imgs[2]} alt={`${title} 3`} className="sd-img-thumb" />
              </div>
            </div>
          )}

          <div className="service-detail-content">
            <div className="service-detail-icon" style={{ color: iconColor }}>
              <Ico name={icon} size={30} />
            </div>
            <div className="section-tag" style={{ marginBottom: 8 }}>
              <span className="section-tag-line" />
              {tag}
            </div>
            <h2 className="section-title">
              {title}<br />
              <span className={color}>{highlight}</span>
            </h2>
            <p className="section-sub" style={{ marginBottom: 24 }}>{desc}</p>
            <ul className="service-feature-list">
              {bullets.map((b, i) => (
                <li key={i}><span className="sfl-dot" />{b}</li>
              ))}
            </ul>
          </div>

          {reverse && (
            <div className="service-detail-imgs">
              <img src={imgs[0]} alt={title} className="sd-img-main" />
              <div className="sd-img-row">
                <img src={imgs[1]} alt={`${title} 2`} className="sd-img-thumb" />
                <img src={imgs[2]} alt={`${title} 3`} className="sd-img-thumb" />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function GlassArtists() {
  return (
    <IndustrySection
      icon="palette"
      iconColor="var(--blue)"
      tag="Industry — Glass Art"
      title="Glass Artists"
      highlight="& Studios"
      color="gold"
      desc="Professional glassblowers, flameworkers, and studios depend on reliable, high-performance torches. We design and build custom burners precisely matched to the heat output, flame profile, and fuel type your art demands."
      bullets={[
        'Custom multi-port flameworking torches',
        'Precise heat output and flame shaping',
        'Safe, pressure-tested builds for studio use',
        'Torch repair and performance tuning',
        'Borosilicate and soft-glass compatible designs',
        'Artist consultations and custom orders',
      ]}
      imgs={[burnerImgs[0], burnerImgs[1], burnerImgs[2]]}
    />
  );
}

function Manufacturing() {
  return (
    <IndustrySection
      reverse
      bg="var(--off-white)"
      icon="building"
      iconColor="var(--blue)"
      tag="Industry — Manufacturing"
      title="Manufacturing &"
      highlight="Fabrication Shops"
      color="blue"
      desc="Production environments require tooling that holds tolerance shift after shift. Our CNC machining, Wire EDM, and custom fabrication services keep your line running with components built to print, on time, every time."
      bullets={[
        'Precision dies, punches, and fixtures',
        'Wire EDM for complex tool steel profiles',
        'Short-run and prototype component production',
        'Replacement parts fabricated to original spec',
        'Welded assemblies and structural fabrication',
        'Fast turnaround to minimize production downtime',
      ]}
      imgs={[autoImgs[0], autoImgs[1], autoImgs[2]]}
    />
  );
}

function ResearchDev() {
  return (
    <IndustrySection
      icon="flask"
      iconColor="var(--blue)"
      tag="Industry — R&D"
      title="Research &"
      highlight="Development Teams"
      color="gold"
      desc="R&D teams need fabrication partners who can move fast, iterate quickly, and hold exact specifications. We work directly with engineers and researchers to produce prototypes and test components that match your design intent exactly."
      bullets={[
        'Rapid prototype fabrication from CAD/drawings',
        'Material selection guidance for test applications',
        'Small-batch production for validation phases',
        'Confidential project handling',
        'Iteration support throughout development cycles',
        'Custom jigs, fixtures, and test rigs',
      ]}
      imgs={[autoImgs[3], autoImgs[4], burnerImgs[3]]}
    />
  );
}

function CustomInnovators() {
  return (
    <IndustrySection
      reverse
      bg="var(--off-white)"
      icon="lightbulb"
      iconColor="var(--gold)"
      tag="Industry — Innovation"
      title="Custom Product"
      highlight="Innovators"
      color="blue"
      desc="Got an idea for a new product but no manufacturing partner? We help inventors and entrepreneurs turn concepts into reality — from the first prototype through pre-production refinement, handling all the fabrication complexity so you can focus on your vision."
      bullets={[
        'Concept-to-prototype in days, not months',
        'Design for manufacturability (DFM) advice',
        'Multiple material and process options explored',
        'Pre-production run support',
        'Packaging and assembly consultation',
        'Scalable solutions as your product grows',
      ]}
      imgs={[burnerImgs[7], burnerImgs[8], autoImgs[5]]}
    />
  );
}
